/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {useTypedSelector} from 'helpers/reducers'
import {useAppDispatch} from 'helpers/store'
import {OffersAction} from 'store/offers/OffersAction'
import {setHeadbarTitle} from 'helpers/actions'
import {resetSelectedOffer} from 'store/offers/OffersReducer'
import {useHeadbarReminders} from 'utils/hooks/useHeadbarReminders'
import OfferOfferNote from 'components/Offers/OfferOfferNote'
import OfferInvoiceNote from 'components/Offers/OfferInvoiceNote'
import OfferBackOfficeNotes from 'components/Offers/OfferBackOfficeNotes'
import EmailTemplatesTab from 'components/EmailTemplates/EmailTemplatesTab'
import OfferDetailInfo from 'pages/Front/Offers/components/OfferDetailInfo'
import OfferDetailActions from 'pages/Front/Offers/components/OfferDetailActions'
import OfferDetailCalculations from 'pages/Front/Offers/components/OfferDetailCalculations'
import {OfferDetailTab} from 'types/enums/specific/OfferDetailTab'
import EditLeadModal from 'components/leads/EditLeadModal'
import EditCompanyModal from 'components/Companies/EditCompanyModal'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiTabSwitcher from 'components/Ui/UiTabSwitcher'
import {UiTabVariant} from 'types/enums/UiTabVariant'
import BookingSellerReservationDetail from 'pages/Front/Booking/Seller/BookingSellerReservationDetail/BookingSellerReservationDetail'
import {useOfferLinkedEntities} from 'utils/hooks/useOfferLinkedEntities'
import {useOfferDetailTabs} from 'utils/hooks/useOfferDetailTabs'
import { ReminderModelName } from 'types/enums/ReminderModelName'
import { Tooltip } from 'react-tooltip'

interface EditOfferModalProps {
    offerId?: number
    onClose?: () => void
}

const EditOfferModal: React.FC<EditOfferModalProps> = (props) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const offerId = props.offerId

    useHeadbarReminders(!!offerId && !Number.isNaN(offerId), ReminderModelName.Offer, offerId || 0)

    const selectedOffer = useTypedSelector((state) => state.offersState.selectedOffer)

    const [isExportingPdf, setIsExportingPdf] = React.useState(false)

    const leadId = selectedOffer?.lead_id || selectedOffer?.lead?.id
    const companyId = selectedOffer?.company_id || selectedOffer?.company?.id
    const bookingFromResponse = (selectedOffer as any)?.booking
    const linkedBookingId = bookingFromResponse?.id ?? selectedOffer?.reservation?.id ?? null

    const hasLinkedOpportunity = !!leadId
    const hasLinkedCompany = !!companyId
    const hasLinkedBooking = !!linkedBookingId

    const { loadedLead, loadedCompany, loadLead, loadCompany } = useOfferLinkedEntities(leadId, companyId)

    const { activeTab, tabs, handleTabClick } = useOfferDetailTabs({
        hasLinkedOpportunity,
        hasLinkedCompany,
        hasLinkedBooking,
        loadLead,
        loadCompany,
    })

    const targetCalculation = selectedOffer?.calculations?.find((c) => c.is_target)

    const getOfferDetailHandler = useCallback(
        (offer_id: number) => {
            dispatch(OffersAction.fetchOfferById(offer_id))
        },
        [dispatch]
    )

    useEffect(() => {
        if (offerId) {
            dispatch(resetSelectedOffer())
            getOfferDetailHandler(offerId)
        }
    }, [dispatch, getOfferDetailHandler, offerId])

    useEffect(() => {
        dispatch(setHeadbarTitle(t('Offer detail')))
        return () => {
            dispatch(setHeadbarTitle(null))
        }
    }, [dispatch, t])

    if (!selectedOffer || selectedOffer.id !== offerId) {
        return (
            <div className={clsx('flex justify-center items-center h-64')}>
                <span className={clsx('text-gray-500')}>{t('Loading offer detail...')}</span>
            </div>
        )
    }

    return (
        <>
            <UiFormModalShell
                className="EditOfferModal OfferDetail"
                title={t('Offer detail')}
                showHeaderCompact={true}
                headerBottom={
                    <UiTabSwitcher
                        items={tabs}
                        variant={UiTabVariant.Underline}
                        selected={activeTab}
                        onClick={(tabId) => handleTabClick(tabId as OfferDetailTab)}
                        className="h-full"
                    />
                }
            >
                {activeTab === OfferDetailTab.Detail && (
                    <div className="OfferDetailTabDetail grid grid-cols-12 gap-3 shrink-0 h-full">
                        <div className="col-span-8 flex flex-col gap-3">
                            <OfferDetailInfo selectedOffer={selectedOffer} />
                            <OfferDetailCalculations />
                        </div>

                        <div className="col-span-4 flex flex-col gap-3 self-start">
                            <OfferDetailActions
                                selectedOffer={selectedOffer}
                                targetCalculation={targetCalculation}
                                isExportingPdf={isExportingPdf}
                                setIsExportingPdf={setIsExportingPdf}
                                onClose={props.onClose}
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <OfferOfferNote offer={selectedOffer} />
                                <OfferInvoiceNote offer={selectedOffer} />
                            </div>
                            <OfferBackOfficeNotes
                                notes={selectedOffer.offerNotes ?? []}
                                offerId={selectedOffer.id}
                                title={t('BackOffice Notes')}
                            />
                        </div>
                    </div>
                )}

                {activeTab === OfferDetailTab.EmailTemplate && (
                    <EmailTemplatesTab
                        modelName="Offer"
                        modelId={selectedOffer.id}
                        context={{
                            offer: selectedOffer,
                            company: selectedOffer.company,
                        }}
                    />
                )}

                {activeTab === OfferDetailTab.Oppurtunity && (
                    <div className="flex-1 overflow-auto">
                        {loadedLead ? (
                            <EditLeadModal
                                lead={loadedLead}
                                onSuccess={() => {
                                    dispatch(OffersAction.fetchOfferById(selectedOffer.id))
                                }}
                                embedded={true}
                            />
                        ) : (
                            <div className="flex justify-center items-center h-64">
                                <span className="text-gray-500">{t('Loading opportunity...')}</span>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === OfferDetailTab.Booking && (
                    hasLinkedBooking && linkedBookingId ? (
                        <div className="h-full">
                            <BookingSellerReservationDetail reservationId={linkedBookingId} embedded={true} />
                        </div>
                    ) : (
                        <div className="flex justify-center items-center h-64">
                            <span className="text-gray-500">{t('No booking linked to this offer')}</span>
                        </div>
                    )
                )}

                {activeTab === OfferDetailTab.Company && (
                    <div className="flex-1 overflow-auto">
                        {loadedCompany ? (
                            <EditCompanyModal
                                company={loadedCompany}
                                embedded={true}
                            />
                        ) : (
                            <div className="flex justify-center items-center h-64">
                                <span className="text-gray-500">{t('Loading company...')}</span>
                            </div>
                        )}
                    </div>
                )}
            </UiFormModalShell>

            <Tooltip id="opportunity-disabled-tooltip" style={{ zIndex: 99999 }} />
            <Tooltip id="company-disabled-tooltip" style={{ zIndex: 99999 }} />
        </>
    )
}

export default EditOfferModal
